import { TaAutocomplete } from '@components/shared/Inputs/TaAutocomplete';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface ServicesAutocompleteProps {
	onSelect: (services: any[]) => void;
	services?: any[];
	userServices?: string[];
	selectedServices: any[];
	resetServices?: boolean;
}

export function ServicesAutocomplete({
	onSelect,
	services,
	userServices,
	selectedServices,
	resetServices,
}: ServicesAutocompleteProps) {
	const transformedServices = services?.flatMap((serviceGroup) =>
		serviceGroup.children.map((service: any) => ({
			id: service.id,
			name: service.name,
			category: serviceGroup.name,
			price: service.price,
			unit: service.unit,
		}))
	);
	const { t } = useTranslation();
	const initializedRef = useRef(false);

	const handleRemoveService = () => {
		onSelect([]);
	};

	const handleSelectService = (service: any | null) => {
		if (service) {
			onSelect([service]);
		} else {
			onSelect([]);
		}
	};

	useEffect(() => {
		if (
			userServices &&
			transformedServices &&
			selectedServices.length === 0 &&
			!initializedRef.current
		) {
			// For single select, just take the first service if available
			if (userServices.length > 0) {
				const serviceId = userServices[0];
				const service = transformedServices.find((service) => service.id === serviceId);
				
				if (service) {
					initializedRef.current = true;
					onSelect([service]);
				}
			}
		}

		if (!userServices || userServices.length === 0) {
			initializedRef.current = false;
		}
	}, [userServices, transformedServices, selectedServices.length, onSelect]);
	
	useEffect(() => {
		if (resetServices && selectedServices.length > 0) {
			onSelect([]);
		}
	}, [resetServices, onSelect, selectedServices.length, selectedServices]);

	// Get the single selected service (if any)
	const selectedService = selectedServices.length > 0 ? selectedServices[0] : null;

	return (
		<div className="flex flex-col gap-[8px]">
			<TaAutocomplete
				multiple={false}
				disableCloseOnSelect={false}
				filterSelectedOptions={true}
				options={transformedServices || []}
				getOptionLabel={(option: any) => option.name}
				groupBy={(option: any) => option.category}
				renderGroup={(params) => (
					<div key={params.group}>
						<div className="text-dark font-roboto text-base font-semibold px-[20px] py-[10px] border-b border-dark">
							{params.group}
						</div>
						{params.children}
					</div>
				)}
				label={t('service')}
				value={selectedService}
				onChange={(_, value) => handleSelectService(value)}
				required
				renderOption={(props, option) => {
					const { key, ...optionProps } = props as any;
					const selected = selectedService && selectedService.id === option.id;

					return (
						<li
							key={key}
							{...optionProps}
							className={`flex items-center justify-between border-b border-dark h-[60px] pr-2 ${selected ? 'bg-[#f5f5f5]' : ''}`}
						>
							<div className="flex items-center gap-[4px] w-full cursor-pointer pl-3">
								<p className="text-secondary font-roboto text-base font-normal">{option.name}</p>
							</div>
							<div className="text-dark w-full max-w-20 text-center font-roboto text-base font-bold h-full bg-disabled px-[16px] py-[20px]">
								€{option.price}
							</div>
						</li>
					);
				}}
			/>
			{selectedService && (
				<div className="flex flex-col gap-[10px]">
					<p className="text-blue font-roboto text-base font-bold mb-[10px]">
						{t('totalPrice', {
							price: selectedService.price,
						})}
					</p>
					<div className="flex flex-col gap-[10px]">
						<div
							key={selectedService.id}
							className="rounded-[10px] border border-dark flex justify-between items-center"
						>
							<p className="text-secondary font-roboto text-base font-normal p-[10px]">
								{selectedService.name}
							</p>
							<div className="flex items-center">
								<div className="text-dark font-roboto border-l border-r border-dark text-base font-bold h-full bg-disabled px-[16px] py-[20px] ">
									€{selectedService.price}
								</div>
								<div className="flex items-center ">
									<IconButton onClick={handleRemoveService}>
										<CloseIcon />
									</IconButton>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
